/**
 * Faithful port of the URL contract in `brouter-web-master/js/router/BRouter.js`.
 *
 * These functions are deliberately free of Angular and Leaflet so they can be
 * verified byte-for-byte against fixtures captured from the legacy client.
 * Any change here is a change to the wire format understood by the BRouter engine.
 */

import {
  LatLng,
  NogoCircle,
  NogoPolygon,
  NogoPolyline,
  Poi,
  RoutingOptions
} from '../models/brouter-map.model';

export const PRECISION = 6;
export const NUMBER_SEPARATOR = ',';
export const GROUP_SEPARATOR = '|';
export const CUSTOM_PREFIX = 'custom_';

/** Equivalent of `L.Util.formatNum`. */
export const formatNum = (num: number, precision = PRECISION): number => {
  const pow = Math.pow(10, precision);
  return Math.round(num * pow) / pow;
};

export const isCustomProfile = (profileName: string | undefined | null): boolean =>
  !!profileName && profileName.substring(0, CUSTOM_PREFIX.length) === CUSTOM_PREFIX;

/** Formats a point as `lng,lat` — note the reversed order versus Leaflet. */
export const formatLatLng = (latLng: LatLng): string =>
  `${formatNum(latLng.lng)}${NUMBER_SEPARATOR}${formatNum(latLng.lat)}`;

export const getLonLatsString = (latLngs: readonly LatLng[]): string =>
  latLngs.map(formatLatLng).join(GROUP_SEPARATOR);

export const parseLonLats = (s: string | undefined | null): LatLng[] => {
  if (!s) {
    return [];
  }
  return s.split(GROUP_SEPARATOR).map(group => {
    const numbers = group.split(NUMBER_SEPARATOR);
    return { lat: Number.parseFloat(numbers[1]), lng: Number.parseFloat(numbers[0]) };
  });
};

/** Serialises the indices of the legs drawn as straight lines. */
export const getBeelineString = (beelineFlags: readonly boolean[]): string => {
  const indexes: number[] = [];
  beelineFlags.forEach((flag, i) => {
    if (flag) {
      indexes.push(i);
    }
  });
  return indexes.join(NUMBER_SEPARATOR);
};

export const parseBeelines = (s: string, lonlats: readonly LatLng[] | undefined): boolean[] => {
  if (!lonlats || lonlats.length < 2) {
    return [];
  }
  const beelineFlags = new Array<boolean>(lonlats.length - 1).fill(false);
  for (const index of s.split(NUMBER_SEPARATOR)) {
    const i = Number.parseInt(index, 10);
    if (!Number.isNaN(i) && i >= 0 && i < beelineFlags.length) {
      beelineFlags[i] = true;
    }
  }
  return beelineFlags;
};

export const getLonLatsNameString = (pois: readonly Poi[]): string =>
  pois
    .map(poi => `${formatLatLng(poi.latlng)}${NUMBER_SEPARATOR}${encodeURIComponent(poi.name)}`)
    .join(GROUP_SEPARATOR);

export const parseLonLatNames = (s: string | undefined | null): Poi[] => {
  if (!s) {
    return [];
  }
  return s.split(GROUP_SEPARATOR).map(group => {
    const part = group.split(NUMBER_SEPARATOR);
    return {
      latlng: { lat: Number.parseFloat(part[1]), lng: Number.parseFloat(part[0]) },
      name: decodeURIComponent(part[2] ?? '')
    };
  });
};

/** `-1` is the default full exclusion and is omitted, matching the legacy client. */
const appendNogoWeight = (s: string, nogoWeight: number | undefined): string =>
  nogoWeight !== undefined && nogoWeight !== null && nogoWeight !== -1
    ? `${s}${NUMBER_SEPARATOR}${nogoWeight}`
    : s;

export const getNogosString = (nogos: readonly NogoCircle[]): string =>
  nogos
    .map(circle =>
      appendNogoWeight(
        `${formatLatLng(circle.center)}${NUMBER_SEPARATOR}${Math.round(circle.radius)}`,
        circle.nogoWeight
      )
    )
    .join(GROUP_SEPARATOR);

export const parseNogos = (s: string | undefined | null): NogoCircle[] => {
  if (!s) {
    return [];
  }
  return s.split(GROUP_SEPARATOR).map(group => {
    // lng,lat,radius(,weight)
    const numbers = group.split(NUMBER_SEPARATOR);
    const nogo: NogoCircle = {
      center: { lat: Number.parseFloat(numbers[1]), lng: Number.parseFloat(numbers[0]) },
      radius: Number.parseFloat(numbers[2])
    };
    if (numbers.length > 3) {
      nogo.nogoWeight = Number.parseFloat(numbers[3]);
    }
    return nogo;
  });
};

const getVerticesString = (vertices: readonly LatLng[]): string =>
  vertices.map(formatLatLng).join(NUMBER_SEPARATOR);

export const getNogosPolylinesString = (nogos: readonly NogoPolyline[]): string =>
  nogos
    .map(polyline => appendNogoWeight(getVerticesString(polyline.vertices), polyline.nogoWeight))
    .join(GROUP_SEPARATOR);

export const getNogosPolygonsString = (nogos: readonly NogoPolygon[]): string =>
  nogos
    .map(polygon => appendNogoWeight(getVerticesString(polygon.vertices), polygon.nogoWeight))
    .join(GROUP_SEPARATOR);

/** Shared by the polyline and polygon parsers: a flat `lng,lat,...` list with an optional trailing weight. */
const parseVertexGroups = (
  s: string | undefined | null
): { vertices: LatLng[]; nogoWeight?: number }[] => {
  if (!s) {
    return [];
  }
  const result: { vertices: LatLng[]; nogoWeight?: number }[] = [];
  for (const group of s.split(GROUP_SEPARATOR)) {
    const numbers = group.split(NUMBER_SEPARATOR);
    if (numbers.length <= 1) {
      continue;
    }
    const vertices: LatLng[] = [];
    let j = 0;
    while (j < numbers.length - 1) {
      const lng = Number.parseFloat(numbers[j++]);
      const lat = Number.parseFloat(numbers[j++]);
      vertices.push({ lat, lng });
    }
    const parsed: { vertices: LatLng[]; nogoWeight?: number } = { vertices };
    if (j < numbers.length) {
      parsed.nogoWeight = Number.parseFloat(numbers[j]);
    }
    result.push(parsed);
  }
  return result;
};

export const parseNogosPolylines = (s: string | undefined | null): NogoPolyline[] =>
  parseVertexGroups(s);

export const parseNogosPolygons = (s: string | undefined | null): NogoPolygon[] =>
  parseVertexGroups(s);

export const parseProfile = (profile: string, profilesRename: Record<string, string>): string =>
  profilesRename[profile] ?? profile;

/** Serialised route state. Values are raw, i.e. not yet URL-encoded. */
export interface BrouterUrlParams {
  lonlats?: string;
  straight?: string;
  pois?: string;
  nogos?: string;
  polylines?: string;
  polygons?: string;
  profile?: string;
  alternativeidx?: number;
  format?: string;
}

export interface BuildUrlParamsInput {
  latLngs: readonly LatLng[];
  beelineFlags?: readonly boolean[];
  pois?: readonly Poi[];
  options: RoutingOptions;
  /** When set, the parameters describe a concrete engine request rather than shareable state. */
  format?: string | null;
  /** The first configured profile; omitted from shareable state because it is the default. */
  defaultProfile: string;
}

/**
 * Port of `getUrlParams`.
 *
 * With no `format`, the result describes shareable route state and drops values that
 * equal their defaults. With a `format`, it describes a concrete engine request and
 * keeps everything.
 */
export const buildUrlParams = ({
  latLngs,
  beelineFlags,
  pois,
  options,
  format,
  defaultProfile
}: BuildUrlParamsInput): BrouterUrlParams => {
  const params: BrouterUrlParams = {};

  params.lonlats = getLonLatsString(latLngs);

  if (beelineFlags && beelineFlags.length > 0) {
    const beelineString = getBeelineString(beelineFlags);
    if (beelineString.length > 0) {
      params.straight = beelineString;
    }
  }

  if (options.nogos?.length) {
    const nogosString = getNogosString(options.nogos);
    if (nogosString.length > 0) {
      params.nogos = nogosString;
    }
  }

  if (options.polylines?.length) {
    const polylinesString = getNogosPolylinesString(options.polylines);
    if (polylinesString.length > 0) {
      params.polylines = polylinesString;
    }
  }

  if (options.polygons?.length) {
    const polygonsString = getNogosPolygonsString(options.polygons);
    if (polygonsString.length > 0) {
      params.polygons = polygonsString;
    }
  }

  if (options.profile != null) {
    params.profile = options.profile;
  }

  if (pois?.length) {
    params.pois = getLonLatsNameString(pois);
  }

  params.alternativeidx = options.alternative;

  if (format != null) {
    params.format = format;
  } else {
    if (params.profile === defaultProfile) {
      delete params.profile;
    }
    if (params.alternativeidx === 0) {
      delete params.alternativeidx;
    }
    // Custom profiles are stored only temporarily on the server, so they are never shared.
    if (params.profile && isCustomProfile(params.profile)) {
      delete params.profile;
    }
  }

  return params;
};

export interface BuildUrlInput extends BuildUrlParamsInput {
  /** Engine base URL, prepended only when a `format` is requested. */
  host: string;
  trackname?: string;
  exportWaypoints?: boolean;
}

/**
 * Port of `getUrl`. Argument order is significant — it is what the captured
 * fixtures encode, and what makes diffs against the legacy client readable.
 */
export const buildUrl = (input: BuildUrlInput): string => {
  const { host, format, trackname, exportWaypoints } = input;
  const urlParams = buildUrlParams(input);
  const args: string[] = [];

  if (urlParams.lonlats != null && urlParams.lonlats.length > 0) {
    args.push(`lonlats=${urlParams.lonlats}`);
  }
  if (urlParams.straight != null) {
    args.push(`straight=${urlParams.straight}`);
  }
  if (urlParams.pois != null && urlParams.pois.length > 0) {
    args.push(`pois=${urlParams.pois}`);
  }
  if (urlParams.nogos != null) {
    args.push(`nogos=${urlParams.nogos}`);
  }
  if (urlParams.polylines != null) {
    args.push(`polylines=${urlParams.polylines}`);
  }
  if (urlParams.polygons != null) {
    args.push(`polygons=${urlParams.polygons}`);
  }
  if (urlParams.profile != null) {
    args.push(`profile=${urlParams.profile}`);
  }
  if (urlParams.alternativeidx != null) {
    args.push(`alternativeidx=${urlParams.alternativeidx}`);
  }
  if (urlParams.format != null) {
    args.push(`format=${urlParams.format}`);
  }
  if (trackname) {
    args.push(`trackname=${trackname}`);
  }
  if (exportWaypoints) {
    args.push('exportWaypoints=1');
  }

  return `${format != null ? host : ''}/brouter?${args.join('&')}`;
};

export interface ParsedRouteState {
  lonlats?: LatLng[];
  beelineFlags?: boolean[];
  nogos?: NogoCircle[];
  polylines?: NogoPolyline[];
  polygons?: NogoPolygon[];
  alternative?: number;
  profile?: string;
  pois?: Poi[];
}

/**
 * Port of `parseUrlParams`, the inverse of {@link buildUrlParams}.
 *
 * Deviation from the legacy client: `alternative` is parsed to a number rather than
 * left as a string. Re-serialising either value yields the same URL, so the round trip
 * is unaffected.
 */
export const parseUrlParams = (
  params: Record<string, string>,
  profilesRename: Record<string, string> = {}
): ParsedRouteState => {
  const opts: ParsedRouteState = {};

  if (params['lonlats']) {
    opts.lonlats = parseLonLats(params['lonlats']);
  }
  if (params['straight']) {
    opts.beelineFlags = parseBeelines(params['straight'], opts.lonlats);
  }
  if (params['nogos']) {
    opts.nogos = parseNogos(params['nogos']);
  }
  if (params['polylines']) {
    opts.polylines = parseNogosPolylines(params['polylines']);
  }
  if (params['polygons']) {
    opts.polygons = parseNogosPolygons(params['polygons']);
  }
  if (params['alternativeidx']) {
    opts.alternative = Number.parseInt(params['alternativeidx'], 10);
  }
  if (params['profile']) {
    opts.profile = parseProfile(params['profile'], profilesRename);
  }
  if (params['pois']) {
    opts.pois = parseLonLatNames(params['pois']);
  }

  return opts;
};
